import { cn } from '@/lib/utils';

interface HtmlContentProps {
  html: string;
  className?: string;
  /** If true, strips HTML tags and renders plain text (for list previews). */
  asPlainText?: boolean;
}

/**
 * Safely renders HTML content (from the rich text editor).
 * Content originates from Tiptap which produces sanitised HTML.
 * Use `asPlainText` for truncated list previews where raw HTML would be unreadable.
 */
export function HtmlContent({ html, className, asPlainText }: HtmlContentProps) {
  if (!html) return null;

  if (asPlainText) {
    // Strip HTML tags for plain text preview
    const text = html.replace(/<[^>]*>/g, '').trim();
    return <span className={className}>{text}</span>;
  }

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none text-[var(--text-secondary)]',
        '[&_h2]:text-base [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-dyndev-green/30 [&_blockquote]:pl-3',
        '[&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4',
        '[&_a]:text-dyndev-green [&_a]:underline',
        '[&_p]:my-1',
        className
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Utility: strip HTML tags for plain text (e.g., list previews, email subjects). */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}
